import React, {useContext, useRef, useState} from "react";
import CropController from "../generated/controllers/cropController";
import CropBrakePointDisplay from "../generated/interfaces/cropBrakePointDisplay";
import CultivarDisplay from "../generated/interfaces/cultivarDisplay";
import PagedSearchTable, {PagedTableFunctions} from "../components/PagedSearchTable";
import NumberNullable from "../components/NumberNullable";
import Dialog from "../components/Dialog";
import Success from "../components/Success";
import Failed from "../components/Failed";
import AppContext from "./appContext";

const CropBrakePoints: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<CropBrakePointDisplay>>()

    // Brake-point edit dialog state
    const [editCrop, setEditCrop] = useState<CropBrakePointDisplay | null>(null)
    const [editShow, setEditShow] = useState(false)
    const [editValue, setEditValue] = useState<number | null>(null)

    // Cultivars dialog state
    const [cultivarCrop, setCultivarCrop] = useState<CropBrakePointDisplay | null>(null)
    const [cultivarShow, setCultivarShow] = useState(false)

    function openEdit(row: CropBrakePointDisplay) {
        setEditCrop(row)
        setEditValue(row.maxTHa)
        setEditShow(true)
    }

    function openCultivars(row: CropBrakePointDisplay) {
        setCultivarCrop(row)
        setCultivarShow(true)
    }

    // Empty value removes the brake point for the crop; a value upserts it.
    function saveEdit() {
        if (editCrop == null) return

        const request = editValue == null
            ? CropController.deleteBrakePoint({id: editCrop.cropId})
            : CropController.upsertBrakePoint({cropId: editCrop.cropId, maxTHa: editValue})

        request.then(() => {
            context.showSnack(<Success title='Success' text='Brake point saved.'/>)
            setEditShow(false)
            pagedTableRef.current?.refresh()
        }).catch(() => {
            context.showSnack(<Failed title='Failed' text='Could not save the brake point.'/>)
        })
    }

    return (
        <div>
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={CropController.paged}
                keyExtractor={row => row.cropId}
                columns={[{
                    header: 'Crop',
                    key: 'cropName',
                    row: row => row.cropName
                }, {
                    header: 'Max T/ha',
                    key: 'maxTHa',
                    row: row => row.maxTHa != null ? row.maxTHa : '-'
                }, {
                    header: <div className="text-right">Actions</div>,
                    key: 'actions',
                    row: row => <div className='flex justify-end gap-1'>
                        <button className='btn btn-sm btn-primary' onClick={() => openEdit(row)}>Edit</button>
                        <button className='btn btn-sm btn-primary' onClick={() => openCultivars(row)}>Cultivars</button>
                    </div>
                }]}/>

            <Dialog title={editCrop ? `Brake point - ${editCrop.cropName}` : 'Brake point'}
                    show={editShow}
                    setShow={setEditShow}
                    body={
                        <div className="p-4 w-[320px]">
                            <label className="block text-sm text-gray-500 mb-1">Max T/ha</label>
                            <NumberNullable value={editValue} change={setEditValue} onEnter={saveEdit}/>
                            <p className="text-xs text-gray-400 mt-2">Leave empty to remove the brake point for this crop.</p>
                        </div>
                    }
                    footer={
                        <div className="flex justify-end gap-1">
                            <button className="btn btn-sm btn-error" onClick={() => setEditShow(false)}>Cancel</button>
                            <button className="btn btn-sm btn-primary" onClick={saveEdit}>Save</button>
                        </div>
                    }/>

            <Dialog title={cultivarCrop ? `Cultivars - ${cultivarCrop.cropName}` : 'Cultivars'}
                    show={cultivarShow}
                    setShow={setCultivarShow}
                    body={
                        cultivarCrop != null
                            ? <div className="p-2 w-[600px] max-w-full">
                                <PagedSearchTable
                                    call={CropController.cultivarsPaged}
                                    buildSearch={base => ({...base, id: cultivarCrop.cropId})}
                                    keyExtractor={(row: CultivarDisplay) => row.id}
                                    columns={[{
                                        header: 'Cultivar',
                                        key: 'name',
                                        row: (row: CultivarDisplay) => row.name
                                    }, {
                                        header: 'Master Data ID',
                                        key: 'masterDataId',
                                        row: (row: CultivarDisplay) => row.masterDataId ?? '-'
                                    }]}/>
                              </div>
                            : null
                    }
                    footer={
                        <div className="flex justify-end">
                            <button className="btn btn-sm btn-error" onClick={() => setCultivarShow(false)}>Close</button>
                        </div>
                    }/>
        </div>
    )
}

export default CropBrakePoints
